import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Button, Modal, Box, Typography, TextField, DialogContentText, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useSelector, useDispatch } from 'react-redux';

import BulkDeleterModalTable from './BulkDeleterModalTable';
import LoadingComponent from '../../../_commons/LoadingComponent';
import api from '@utils/axiosInstance';

dayjs.extend(utc);
dayjs.extend(timezone);

function BulkDeleterCreateModal({ open, setOpen }) {
    const { t } = useTranslation();

    // dayjs 로케일을 동적으로 설정
    const currentLocale = t('common.locale') || 'ko';
    dayjs.locale(currentLocale);

    const [startDate, setStartDate] = useState(dayjs().subtract(1, 'day').startOf('day'));
    const [endDate, setEndDate] = useState(dayjs().subtract(1, 'day').endOf('day'));
    const [userTimeZone, setUserTimeZone] = useState('');
    const [timeZoneOffset, setTimeZoneOffset] = useState('');
    const [resultState, setResultState] = useState('');

    const [contentList, setContentList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [noData, setNoData] = useState(false);

    const userToken = useSelector(state => state.organization.userToken);
    const organizationData = useSelector(state => state.organization.organizationData);

    const PER_PAGE = 30;

    const handleStartDateChange = (newStartDate) => {
        if (newStartDate && (!endDate || newStartDate.isBefore(endDate))) {
            setStartDate(newStartDate.startOf('day'));
        }
    };

    const handleEndDateChange = (newEndDate) => {
        if (newEndDate && newEndDate.isAfter(startDate)) {
            setEndDate(newEndDate.endOf('day'));
        }
    };

    useEffect(() => {
        if (open) {
            const detectedTimeZone = dayjs.tz.guess();
            setUserTimeZone(detectedTimeZone);

            const utcOffset = dayjs().tz(detectedTimeZone).format('Z');
            if (utcOffset === '+09:00') {
                setTimeZoneOffset('KST');
            } else if (utcOffset === '+00:00') {
                setTimeZoneOffset('UTC');
            } else {
                setTimeZoneOffset(`UTC${utcOffset}`);
            }

            var tmpStartDate = dayjs().tz(detectedTimeZone).subtract(1, 'day').startOf('day');
            var tmpEndDate = dayjs().tz(detectedTimeZone).subtract(1, 'day').endOf('day');
            setStartDate(tmpStartDate);
            setEndDate(tmpEndDate);
        }
    }, [open]);

    const getContentList = async (page, start_date, end_date) => {
        setIsLoading(true);

        try {
            const response = await api({
                method: 'post',
                url: '/batchRemover/getContentList',
                data: {
                    tk: userToken,
                    ogc: organizationData.organization_code,
                    type: "vod",
                    data: {
                        timezone: timeZoneOffset,
                        start_date: start_date,
                        end_date: end_date,
                        page: page,
                        perPage: PER_PAGE
                    }
                }
            });

            if (response.data.code === 100) {
                setContentList(response.data.list.data);
                setTotalCount(response.data.list.totalItems);
                setNoData(response.data.list.data.length === 0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        getContentList(newPage, startDate, endDate);
    };

    const handleSetWork = () => {
        setResultState('ready');

        api({
            method: 'post',
            url: '/batchRemover/setWork',
            data: {
                tk: userToken,
                ogc: organizationData.organization_code,
                type: "vod",
                data: {
                    timezone: timeZoneOffset,
                    start_date: startDate,
                    end_date: endDate
                }
            }
        })
            .then((response) => {
                if (response.data.code === 100) {
                    setResultState('success');
                    handleClose();
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const handleClose = () => {
        setStartDate(dayjs().subtract(1, 'day').startOf('day'));
        setEndDate(dayjs().subtract(1, 'day').endOf('day'));
        setUserTimeZone('');
        setTimeZoneOffset('');
        setResultState('');
        setContentList([]);
        setTotalCount(0);
        setPage(1);
        setIsLoading(false);
        setNoData(false);
        setOpen(false);
    };

    return (
        <Modal
            className="bulkdeleter-modal-root"
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
            disablePortal
        >
            <Box className="bulkdeleter-modal-container">
                <Box className="bulkdeleter-modal-header">
                    <Typography className="bulkdeleter-modal-title" variant="h6" component="h2">
                        {t('vod.bulk_deleter.title')}
                    </Typography>
                </Box>
                <Box className="bulkdeleter-modal-content">
                    <Box className="bulkdeleter-modal-timezone-section">
                        <Typography variant="body1" className="bulkdeleter-modal-timezone-text">
                            {t('vod.bulk_deleter.user_timezone')}: {timeZoneOffset}
                            <Typography variant="caption" component="span" className="bulkdeleter-modal-timezone-caption">
                                ( {userTimeZone} )
                            </Typography>
                        </Typography>
                    </Box>

                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocale}>
                        <Box className="bulkdeleter-modal-date-section">
                            <DateTimePicker
                                label={t('vod.bulk_deleter.start_date')}
                                value={startDate}
                                onChange={handleStartDateChange}
                                views={['year', 'month', 'day']}
                                slots={{
                                    textField: TextField,
                                }}
                                slotProps={{
                                    textField: {
                                        variant: "outlined",
                                        className: "bulkdeleter-modal-date-picker",
                                    },
                                }}
                                format="YYYY-MM-DD HH:mm:ss"
                                maxDate={endDate}
                            />
                            <DateTimePicker
                                label={t('vod.bulk_deleter.end_date')}
                                value={endDate}
                                onChange={handleEndDateChange}
                                views={['year', 'month', 'day']}
                                slots={{
                                    textField: TextField,
                                }}
                                slotProps={{
                                    textField: {
                                        variant: "outlined",
                                        className: "bulkdeleter-modal-date-picker",
                                    },
                                }}
                                format="YYYY-MM-DD HH:mm:ss"
                                minDate={startDate}
                            />
                            <Button variant="contained" color="primary" className="bulkdeleter-modal-fetch-btn" onClick={() => { getContentList(1, startDate, endDate), setPage(1) }}>
                                {t('vod.bulk_deleter.fetch')}
                            </Button>
                        </Box>
                    </LocalizationProvider>
                </Box>

                <Box id="box-table-batch-remover" className="bulkdeleter-modal-table-section">
                    <div>
                        {resultState === "ready" ?
                            <DialogContentText id="alert-dialog-description" className="bulkdeleter-modal-loading-container">
                                <CircularProgress size={20} className="bulkdeleter-modal-loading-spinner" />
                                {t('vod.bulk_deleter.saving')}
                            </DialogContentText>
                            :
                            isLoading ?
                                <LoadingComponent />
                                : contentList.length === 0 && !noData ? (
                                    <Typography variant="body1" align="center" color="textSecondary" className="bulkdeleter-modal-select-message">
                                        {t('vod.bulk_deleter.select_and_fetch')}
                                    </Typography>
                                ) : (
                                    <BulkDeleterModalTable
                                        contentList={contentList}
                                        totalCount={totalCount}
                                        page={page}
                                        isLoading={isLoading}
                                        noData={noData}
                                        onPageChange={handlePageChange}
                                    />
                                )
                        }
                    </div>
                </Box>

                <Box className="bulkdeleter-modal-button-section">
                    <Button color="info" onClick={handleClose} className="bulkdeleter-modal-cancel-btn">
                        {t('vod.bulk_deleter.cancel')}
                    </Button>
                    <Button variant="contained" color="info" onClick={handleSetWork}>
                        {t('vod.bulk_deleter.save')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

export default BulkDeleterCreateModal;